import { useState, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
  RouteAnalysisResponse,
  FuelStrategyResponse,
  FuelIntervalResponse,
  FuelIntervalRequest,
  ProductInfo,
  StrategyOptions,
} from '../../models';
import { useLocaleContext } from '../../i18n';
import { generateSticker } from '../../api/Api';
import GpxUpload from './GpxUpload';
import StrategyOptionsForm from './StrategyOptionsForm';
import FuelIntervalEditor from './FuelIntervalEditor';
import ProductsForm from './ProductsForm';
import FuelStrategyResults from './FuelStrategyResults';
import FuelPlanSummary from './FuelPlanSummary';
import StepIndicator from './StepIndicator';

type Step = 1 | 2 | 3 | 4 | 5 | 6;

export default function FuelPlanner() {
  const { locale } = useLocaleContext();
  const [step, setStep] = useState<Step>(1);
  const [route, setRoute] = useState<RouteAnalysisResponse | null>(null);
  const [strategyOptions, setStrategyOptions] = useState<StrategyOptions | null>(null);
  const [fuelIntervals, setFuelIntervals] = useState<FuelIntervalResponse[] | null>(null);
  const [totalDuration, setTotalDuration] = useState<string | null>(null);
  const [strategy, setStrategy] = useState<FuelStrategyResponse | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [formProducts, setFormProducts] = useState<ProductInfo[]>([]);
  const [stickerUrl, setStickerUrl] = useState<string | null>(null);
  const [stickerLoading, setStickerLoading] = useState(false);
  const stickerUrlRef = useRef<string | null>(null);

  const bidonMl = 500;

  const handleRouteComplete = (r: RouteAnalysisResponse) => {
    setRoute(r);
    setStep(2);
  };

  const handleOptionsComplete = (options: StrategyOptions, intervals: FuelIntervalResponse[], duration: string) => {
    setStrategyOptions(options);
    setFuelIntervals(intervals);
    setTotalDuration(duration);
    setEditorKey(k => k + 1);
    setStep(3);
  };

  const handleIntervalsComplete = (intervals: FuelIntervalResponse[], duration: string) => {
    setFuelIntervals(intervals);
    setTotalDuration(duration);
    setStep(4);
  };

  const handleStrategyComplete = (s: FuelStrategyResponse) => {
    setStrategy(s);
    setStep(6);
  };

  const handleBackToResults = () => {
    setStep(5);
  };

  const handleReset = () => {
    setStrategyOptions(null);
    setFuelIntervals(null);
    setTotalDuration(null);
    setStrategy(null);
    setRoute(null);
    setStep(1);
  };

  const handleBackToSettings = () => {
    setStep(2);
  };

  const handleBackToIntervals = () => {
    setStep(3);
  };

  const handleBackToProducts = () => {
    setStep(4);
  };

  const handleBackToRoute = () => {
    setStep(1);
  };

  const handleGenerateSticker = async () => {
    if (!strategy || !route) return;
    setStickerLoading(true);
    try {
      const elevationProfile = route.segments.map(seg => ({
        km: seg.endKm,
        elevationM: Math.round(seg.distanceKm * seg.gradientPercent),
      }));

      const stickerRequest = {
        title: 'Fuel Plan',
        totalDistanceKm: route.totalDistanceKm,
        totalElevationGainM: route.totalElevationGainM,
        elevationProfile,
        fuelPoints: strategy.points.map(p => ({
          km: p.kilometerMark,
          products: p.products.map(prod => `${prod.name} ${prod.quantity}g`),
          totalCarbs: p.products.reduce((sum, prod) => sum + prod.quantity, 0),
        })),
      };
      const blob = await generateSticker(stickerRequest);
      const url = window.URL.createObjectURL(blob);
      stickerUrlRef.current = url;
      setStickerUrl(url);
    } catch (error) {
      console.error('Failed to generate sticker:', error);
    } finally {
      setStickerLoading(false);
    }
  };

  const handleCloseStickerModal = () => {
    if (stickerUrlRef.current) {
      window.URL.revokeObjectURL(stickerUrlRef.current);
      stickerUrlRef.current = null;
    }
    setStickerUrl(null);
  };

  const handleDownloadSticker = () => {
    if (!stickerUrl) return;
    const link = document.createElement('a');
    link.href = stickerUrl;
    link.download = 'fuel-plan-sticker.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fuelIntervalRequests: FuelIntervalRequest[] = (fuelIntervals ?? []).map(i => ({
    kilometerMark: i.kilometerMark,
    targetCarbs: i.targetCarbs,
    estimatedTime: i.estimatedTime,
    burnedKcal: i.burnedKcal,
  }));

  const handleStepClick = (clickedStep: Step) => {
    const handlers: Record<Step, () => void> = {
      1: handleBackToRoute,
      2: handleBackToSettings,
      3: handleBackToIntervals,
      4: handleBackToProducts,
      5: handleReset,
      6: handleReset,
    };
    handlers[clickedStep]();
  };

  const STEPS: Array<{ num: 1 | 2 | 3 | 4 | 5 | 6; label: string }> = [
    { num: 1, label: locale.planner.steps.routeAnalysis },
    { num: 2, label: locale.planner.steps.strategyOptions },
    { num: 3, label: locale.planner.steps.fuelPoints },
    { num: 4, label: locale.planner.steps.products },
    { num: 5, label: locale.planner.steps.fuelPlan },
    { num: 6, label: locale.planner.steps.summary },
  ];

  return (
    <div style={{ width: '100%', paddingBottom: '200px' }} className="pt-0">
      <StepIndicator
        steps={STEPS}
        currentStep={step}
        onStepClick={handleStepClick}
      />

      {step === 1 && <GpxUpload initialRoute={route} onComplete={handleRouteComplete} />}
      {step === 2 && route && (
        <StrategyOptionsForm
          route={route}
          initialOptions={strategyOptions ?? undefined}
          onComplete={handleOptionsComplete}
          onBack={() => setStep(1)}
        />
      )}
      {step === 3 && route && strategyOptions && fuelIntervals && totalDuration && (
        <FuelIntervalEditor
          key={editorKey}
          route={route}
          strategyOptions={strategyOptions}
          intervals={fuelIntervals}
          totalDuration={totalDuration}
          onComplete={handleIntervalsComplete}
          onBack={handleBackToSettings}
        />
      )}
      {step === 4 && strategyOptions && fuelIntervals && totalDuration && (
        <ProductsForm
          fuelIntervals={fuelIntervalRequests}
          totalDuration={totalDuration}
          strategyOptions={strategyOptions}
          onComplete={handleStrategyComplete}
          onBack={handleBackToIntervals}
          onProductsChange={setFormProducts}
        />
      )}
      {step === 5 && strategy && route && strategyOptions && (
        <FuelStrategyResults
          strategy={strategy}
          route={route}
          strategyOptions={strategyOptions}
          onBack={handleBackToProducts}
          onNext={() => setStep(6)}
          bidonMl={bidonMl}
          formProducts={formProducts}
        />
      )}
      {step === 6 && strategy && route && strategyOptions && (
        <FuelPlanSummary
          strategy={strategy}
          route={route}
          strategyOptions={strategyOptions}
          totalDuration={totalDuration ?? 'PT0S'}
          onBack={handleBackToResults}
          onGenerateSticker={handleGenerateSticker}
          stickerLoading={stickerLoading}
        />
      )}

      <Modal
        show={!!stickerUrl}
        onHide={handleCloseStickerModal}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{locale.planner.summary.stickerTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-2">
          {stickerUrl && <img src={stickerUrl} alt="Fuel plan sticker" style={{ maxWidth: '100%' }} />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-info" onClick={handleDownloadSticker}>
            {locale.planner.summary.downloadSvg}
          </Button>
          <Button variant="secondary" onClick={handleCloseStickerModal}>
            {locale.planner.close}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
